/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, useEffect } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { isEmpty } from "lodash"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { SortableItemList } from "components/common"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import { debounceSortHandler } from "utils/misc"
import { EventAction, EventCategory } from "constants/tracking"
import { MENU_DETAIL } from "constants/routes"
import { getUCMConfig } from "utils/config"
import { fullPathWithSearchParams } from "common/utils/uri"
import { Item } from "types/models/menu"
import useInventoryContext from "hooks/useInventoryContext"
import { sortMenuItemNewLogic } from "utils/sort_helper"
import commonStyles from "styles/common.modules.css"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"
import { EmptyItems } from "../../empty/EmptyItems"
import { MenuItemRow } from "./MenuItemRow"
import { MenuItemRowLoading } from "./MenuItemRowLoading"
import { useMenuOverviewItems } from "../hooks/useMenuOverviewItems"

const rowHeight = 128

interface IProps {
  hasAction: boolean
  routePrefix?: string
}

export const MenuOverviewItems: FC<IProps> = ({
  hasAction,
  routePrefix,
}: IProps) => {
  const dispatch = useDispatch()

  const history = useHistory()
  const { itemID: itemParam } = useParams<{
    itemID: string
  }>()
  const { isEditAllowed } = useMenuEditorStatus()
  const { items, filteredItems, isLoading } = useMenuOverviewItems()

  const {
    merchantID,
    isMartDepartmentStructure,
    overview: {
      itemID,
      category: selectedCategory,
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
    },
    isMartStore,
    drawer: { showItemSelection },
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
    isUCS,
  } = useSelector((state: AppState) => state?.menu)

  // Check if a department/category is selected
  // EmptyItems should only show when a selection exists but has no items
  const hasSelection = isMartDepartmentStructure
    ? !!selectedSubDepartment
    : !!selectedCategory

  const handleMenuClick = (key: string) => {
    switch (key) {
      case "rearrange":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showSortItems: true,
          }),
        )
        break
      case "multiple":
        dispatch(
          actionCreator.menu.toggleDrawer({
            showItemSelection: true,
          }),
        )
        break
      default:
        break
    }
  }

  const showItemDrawer = (item: Item, sender: string = "") => {
    const clickEditTimestamp = new Date().valueOf()

    const nextPath = `${MENU_DETAIL({
      isMartStore,
      merchantID: menuGroupID || merchantID,
      tabKey: "menuOverview",
      itemID: item.itemID,
      customRoutePrefix: routePrefix,
    })}`
    const destination = fullPathWithSearchParams({
      nextPath,
    })
    history.replace({
      ...destination,
    })

    dispatch(
      actionCreator.menu.toggleDrawer({
        item,
        showItem: true,
        sourceShowItem: EventCategory.menuOverviewModifyItem,
        showUploadPhoto: sender === "addButton",
      }),
    )

    let extraLabels = {}
    if (TroyWebTracker.isMenuGroup) {
      extraLabels = {
        ...extraLabels,
        CLICK_EDIT_TIMESTAMP: clickEditTimestamp,
        EDIT_SHOWN_TIMESTAMP: new Date().valueOf(),
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickEditItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ITEM_ID: item.itemID,
        ...extraLabels,
        TRAFFIC_MEDIUM: sender === "addButton" ? "MISSING_PHOTO" : "",
      },
    })
  }

  const { disableAvailability } = useInventoryContext()

  const renderRow = (item: Item) => {
    return (
      <MenuItemRow
        onClickAddPhoto={() => {
          showItemDrawer(item, "addButton")
        }}
        item={item}
        showSelection={showItemSelection}
        showDropdown={hasAction && !disableAvailability}
      />
    )
  }

  const textMap = useTranslationsMap({
    rearrange: "menu.drawers.sort.items",
    selectMultiple: "menu.actions.selectMultiple",
    sortItemsSuccess: "menu.notifications.sortItems.successMessage",
  })

  // deeplink when there is itemParam on first page load
  useEffect(() => {
    if (itemParam) {
      const selectedItem = filteredItems.find(
        (item: Item) => item.itemID === itemParam,
      )
      if (selectedItem) {
        showItemDrawer(selectedItem)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemParam, filteredItems.length])

  const onSortEnd = debounceSortHandler(
    (sortedItems: Item[], newIndex: number) => {
      const categoryID = selectedCategory?.categoryID ?? ""
      const departmentID = selectedSubDepartment?.itemClassID ?? ""

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewRearrangeItem,
        action: EventAction.submitRearrangeItem,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          CATEGORY_ID: !isMartDepartmentStructure ? categoryID : departmentID,
        },
      })

      const sorts = isUCS
        ? sortMenuItemNewLogic({
            newIndex,
            newList: sortedItems,
            sortKey: "sortOrder",
            itemKey: "itemID",
          })
        : sortedItems.map((item, index) => ({
            resourceID: item.itemID ?? "",
            sortOrder: sortedItems.length - index,
          }))

      dispatch(
        !isMartDepartmentStructure
          ? actionCreator.menu.sortItems({
              merchantID,
              categoryID,
              sorts,
              successMessage: textMap.sortItemsSuccess,
              merchantGroupID,
              menuGroupID,
            })
          : actionCreator.menu.sortDepartmentItems({
              merchantID,
              departmentID,
              sorts,
              successMessage: textMap.sortItemsSuccess,
              menuGroupID,
              merchantGroupID,
            }),
      )
    },
  )

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
      title={
        <Row justify="space-between">
          <Col>{useTranslation("menu.headings.items")}</Col>
          {hasAction && getUCMConfig("enableTableMenuButton") && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{
                  items: [
                    ...(isEditAllowed &&
                    !(
                      !isEmpty(itemID) ||
                      filteredItems.length === 0 ||
                      [
                        selectedCategory,
                        selectedDepartment,
                        selectedSubDepartment,
                      ].every(isEmpty)
                    )
                      ? [
                          {
                            key: "rearrange",
                            label: textMap.rearrange,
                            onClick: () => handleMenuClick("rearrange"),
                          },
                        ]
                      : []),
                    {
                      key: "multiple",
                      label: textMap.selectMultiple,
                      onClick: () => handleMenuClick("multiple"),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      {isLoading && <MenuItemRowLoading />}
      {!isLoading && (
        <div
          css={css`
            height: 100%;
            ${!hasAction &&
              css`
                pointer-events: none;
              `}
          `}
        >
          {hasSelection && items.length === 0 ? (
            <EmptyItems />
          ) : (
            <SortableItemList
              items={filteredItems}
              renderRowContent={renderRow}
              useDragHandle
              itemSize={rowHeight}
              onRowClick={(_, item: Item) => showItemDrawer(item)}
              draggable={getUCMConfig("enableDraggableList")}
              onSort={({ newList, newIndex }) => {
                onSortEnd(newList, newIndex)
              }}
            />
          )}
        </div>
      )}
    </Card>
  )
}
